/**
 * Confetti burst for the moment the free-shipping threshold is met.
 *
 * Hand-rolled on one canvas rather than pulling in canvas-confetti. The library is only ~5KB, but
 * BUILDSPEC §10 keeps the script stack deliberately small and this is seventy lines.
 *
 * Cost when it is not firing: nothing at all. No canvas, no animation loop, no listeners — the
 * whole thing is a function that the shipping tracker calls on one specific state change. It never
 * runs during a page load, so it cannot touch LCP or the Lighthouse score, and the canvas is a
 * fixed-position overlay so it cannot shift layout.
 *
 * Colours are the brand palette, with Blush left out: the cart, the announcement bar and most of
 * the site are Blush, so those particles would be invisible against their own background.
 *
 * Liam waived the §3 "never mix Deep Teal with any pink" pairing rule for this effect (7 Aug).
 *
 * Exposed as window.mtsCelebrate so the tracker can call it without a module graph.
 */
(function () {
  'use strict';

  var COLOURS = ['#FDD4E1', '#CEF2F2', '#C13A66', '#157A82', '#35242E'];
  var GRAVITY = 0.26;
  var DRAG = 0.992;
  var MAX_MS = 4500;
  var SPREAD = 1.2;

  var running = false;

  function makeParticle(originX, originY, angle, spread, power) {
    var a = angle + (Math.random() - 0.5) * spread;
    // Floor raised from 0.65 so fewer pieces fizzle out near the corner they launched from.
    var v = power * (0.78 + Math.random() * 0.5);

    return {
      x: originX,
      y: originY,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v,
      w: 6 + Math.random() * 6,
      h: 9 + Math.random() * 7,
      rot: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.28,
      tilt: Math.random() * Math.PI * 2,
      tiltSpeed: 0.08 + Math.random() * 0.1,
      colour: COLOURS[(Math.random() * COLOURS.length) | 0],
    };
  }

  window.mtsCelebrate = function () {
    // §5 and §10 both require motion to respect this. Not a nice-to-have.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (running) return;
    running = true;

    var w = window.innerWidth;
    var h = window.innerHeight;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    var canvas = document.createElement('canvas');
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483000';
    document.body.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Scaled to the viewport so a phone is not asked to push desktop numbers.
    var count = w < 700 ? 110 : 170;
    var third = Math.round(count / 3);
    var particles = [];
    var i;

    // Launch speed is derived from the viewport rather than fixed, so the burst reaches the top of
    // the page whatever the screen: at 60fps a particle rises v²/2g pixels, so v = √(2gh) just
    // clears the fold, and the extra 20% carries the fastest pieces past it. A fixed number looked
    // right on a laptop and barely left the corner on a tall desktop window.
    var power = Math.sqrt(2 * GRAVITY * h) * 1.2;

    // Three cannons — the lower corners firing up and inward, plus one straight up from the middle
    // to fill the centre, which two corner cannons leave sparse until their arcs cross.
    for (i = 0; i < third; i++) particles.push(makeParticle(0, h, -Math.PI / 3.4, SPREAD, power));
    for (i = 0; i < third; i++) particles.push(makeParticle(w, h, (-Math.PI * 2) / 3.4 - 0.35, SPREAD, power));
    for (i = 0; i < count - third * 2; i++) {
      particles.push(makeParticle(w / 2, h, -Math.PI / 2, SPREAD * 1.5, power * 0.92));
    }

    var start = null;
    var frame = null;

    function stop() {
      if (frame) cancelAnimationFrame(frame);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      running = false;
    }

    function tick(now) {
      if (start === null) start = now;
      ctx.clearRect(0, 0, w, h);

      var alive = 0;

      for (var j = 0; j < particles.length; j++) {
        var p = particles[j];

        p.vy += GRAVITY;
        p.vx *= DRAG;
        p.vy *= DRAG;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.spin;
        p.tilt += p.tiltSpeed;

        if (p.y - p.h > h) continue;
        alive++;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        // Squashing the width on a sine makes each piece read as a flat strip turning over,
        // rather than a rectangle sliding about.
        ctx.scale(Math.abs(Math.cos(p.tilt)) * 0.8 + 0.2, 1);
        ctx.fillStyle = p.colour;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      if (alive === 0 || now - start > MAX_MS) {
        stop();
        return;
      }

      frame = requestAnimationFrame(tick);
    }

    frame = requestAnimationFrame(tick);
  };
})();
